import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import { Share } from "@capacitor/share";
import FileSaver from "file-saver";
import { SendIntent } from "send-intent";
import { Filesystem } from "@capacitor/filesystem";
import { useAuthStore } from "../pinia-stores/auth.js";
import i18n from "../i18n.js";
import { useDesktopScreen, useTabletScreen } from "./useMediaQuery.js";

export function useSocialShare() {
  const authStore = useAuthStore();
  const t = i18n.global.t;

  const phoneNumberDetail = computed(() => authStore.phoneNumberDetail);
  const mobilePhoneError = computed(() => authStore.mobilePhoneError);
  const profile = computed(() => authStore.profile);
  const isDeskTop = useDesktopScreen();
  const isTablet = useTabletScreen().matches;
  const dynamicLink = computed(() => authStore.dynamicLink);
  const keycloak = computed(() => authStore.keycloak);
  const authenticated = computed(() => authStore.authenticated);

  let actionsToPopover = null;

  function onClickShare(shareContent) {
    console.log("share content", shareContent);
    if (!actionsToPopover) {
      actionsToPopover = f7.actions.create({
        convertToPopover: true,
        buttons: [
          [
            {
              text: t("common.share"),
              color: "auto",
              bold: true,
            },
            {
              text: t("common.shareInAvochat"),
              icon: '<i class="icon elevation-3"><img src="/32x32.png" alt="avocash icon" height="32" width="32"></i>',
              onClick() {
                avochatShare(shareContent);
              },
            },
            {
              text: t("common.shareInFairia"),
              icon: '<i class="icon elevation-3"><img src="/32x32.png" alt="avocash icon" height="32" width="32"></i>',
              onClick() {
                fairiaShare(shareContent);
              },
            },
            {
              text: t("common.shareMoreOptions"),
              icon: '<i class="f7-icons color-auto">arrow_branch</i>',
              onClick() {
                socialShareFn(shareContent);
              },
            },
          ],
          [
            {
              text: t("common.cancel"),
              color: "red",
            },
          ],
        ],
        targetEl: ".shareButton",
      });
    }
    actionsToPopover.open();
    socialShareFn(shareContent);
  }

  function avochatShare(options) {
    const params = new URLSearchParams();
    if (options.subject) params.set("title", options.subject);
    if (options.message) params.set("text", options.message);
    if (options.url) params.set("url", options.url);
    const query = params.toString();
    if (f7router && f7router.navigate) {
      f7router.navigate(`/share-target${query ? "?" + query : ""}`);
    } else {
      window.location.href = `/share-target${query ? "?" + query : ""}`;
    }
  }

  function fairiaShare(options) {
    avochatShare(options);
  }

  async function socialShareFn(shareContent) {
    console.log("shareContent", shareContent);
    const { files, subject, message, url } = shareContent;
    const shareOptions = {
      title: subject,
      text: message,
      url,
      dialogTitle: t("common.pickanAppToShare"),
    };

    try {
      if (files && files.length > 0) {
        if (Share.canShare && Share.canShare({ files })) {
          await Share.share({ ...shareOptions, files });
          console.log("Share was successful.");
          return;
        } else if (navigator.canShare && navigator.canShare({ files })) {
          await navigator.share({ ...shareOptions, files });
          console.log("Shared!", shareContent);
          return;
        } else {
          await Share.share(shareOptions);
          console.log("Your system doesn't support sharing files.");
        }
      } else {
        console.log("No files to share, using text and url only.");
        if (Share.canShare && Share.canShare(shareOptions)) {
          await Share.share(shareOptions);
          console.log(
            "Share was successful with Share plugin!",
            shareContent
          );
          return;
        } else if (navigator.canShare && navigator.canShare(shareOptions)) {
          await navigator.share(shareOptions);
          console.log("Shared with navigator!", shareContent);
          return;
        }
      }
    } catch (error) {
      console.log("Sharing failed", error);
    }
  }

  function saveFile(fileToSave) {
    console.log(fileToSave);
    for (const file of fileToSave) {
      FileSaver.saveAs(file);
    }
  }

  async function acceptSendIntent() {
    await SendIntent.checkSendIntentReceived()
      .then((result) => {
        if (result) {
          console.log("SendIntent received");
          console.log(JSON.stringify(result));
        }
        if (result.url) {
          const resultUrl = decodeURIComponent(result.url);
          Filesystem.readFile({ path: resultUrl })
            .then((content) => {
              console.log(content.data);
            })
            .catch((err) => console.error(err));
        }
      })
      .catch((err) => console.error(err));

    SendIntent.finish();
  }

  onMounted(() => {
    window.addEventListener("sendIntentReceived", () => {
      acceptSendIntent();
    });
  });

  return {
    onClickShare,
    avochatShare,
    fairiaShare,
    socialShare: socialShareFn,
    saveFile,
    acceptSendIntent,
    phoneNumberDetail,
    mobilePhoneError,
    profile,
    isDeskTop,
    isTablet,
    dynamicLink,
    keycloak,
    authenticated,
  };
}
